// Central navigation model — single source of truth for the desktop sidebar,
// mobile office drawer, breadcrumbs and collapsed-rail flyouts.
// Visibility rules are UNCHANGED from the flat nav: same roles + feature
// flags (flags only restrict `field` users, matching the original logic).
import {
  LayoutDashboard, Clock, ShieldAlert, Hammer, Users, Truck, CalendarDays,
  UserCog, Banknote, ClipboardCheck, Wrench, ShieldCheck, FileCheck2, Calendar,
  ClipboardList, Package, Boxes, Receipt, FileSignature, FileText, Library, Layers,
  MapPin, Trophy, Heart, Phone, BarChart3, Megaphone, TrendingUp,
  Fuel, ShoppingCart, PencilRuler, HardHat, LayoutGrid, UploadCloud, Mail, Compass, Video, Recycle, SlidersHorizontal, FlaskConical, Star, Mountain, Building2, Archive,
} from "lucide-react";

const ALL = ["super_admin", "admin", "office", "field", "mechanic", "quantity_surveyor"];
const OFFICE = ["super_admin", "admin", "office"];
export const canAccessHealthSafety = (user) => OFFICE.includes(user?.role);
const OFFICE_QS = ["super_admin", "admin", "office", "quantity_surveyor"];
const OFFICE_FIELD = ["super_admin", "admin", "office", "field"];
const SY_ROLES = ["super_admin", "admin", "office", "foreman", "quantity_surveyor"];
const SY_GATE = ["super_admin", "admin", "office", "foreman", "weighbridge_operator"];
const SY_LOADER = ["super_admin", "admin", "office", "foreman", "loader_driver"];
const SY_ALL = ["super_admin", "admin", "office", "foreman", "quantity_surveyor", "weighbridge_operator", "loader_driver"];

export const GROUP_RAIL_ICONS = {
  hs_site: HardHat, hs_risk: ShieldAlert, hs_assurance: ClipboardCheck, hs_people: Users, hs_documents: FileText,
  driver_tools: Truck, trucking_operations: Truck, trucking_fleet: Truck, trucking_commercial: Receipt, trucking_management: BarChart3,
  work: Hammer, supply_yard: Boxes, commercial: Receipt, workforce: HardHat, safety: ShieldAlert,
  assets: Truck, contacts: Users, more: LayoutGrid,
};

// Item shape: { to, label, icon, roles, flag?, end?, testId, badgeNew? }
// Submenu shape: { id, label, icon, testId, children: [items] } — a submenu is
// visible when at least one child is visible; a single visible child renders
// as a direct link instead of a one-item submenu.
export const NAV_GROUPS = [
  {
    id: "home",
    label: null,
    items: [
      { to: "/app", label: "Overview", icon: LayoutDashboard, roles: ALL, end: true, testId: "nav-overview" },
    ],
  },
  {
    id: "work",
    label: "Run the Work",
    items: [
      { to: "/app/operations-map", label: "Operations", icon: MapPin, roles: OFFICE, testId: "nav-operations-map" },
      { to: "/app/jobs", label: "Jobs", icon: Hammer, roles: [...OFFICE_FIELD, "quantity_surveyor"], testId: "nav-jobs" },
      { to: "/app/materials", label: "Materials", icon: Boxes, roles: [...OFFICE_FIELD, "quantity_surveyor"], testId: "nav-materials" },
      { to: "/app/crew-planner", label: "Crew Planner", icon: Calendar, roles: OFFICE, testId: "nav-crew-planner" },
      { to: "/app/crew-needs", label: "Crew Needs", icon: ShoppingCart, roles: OFFICE, testId: "nav-crew-needs" },
    ],
  },
  { id: "driver_tools", label: "My Trucking", items: [
    { to: "/app/my-runs", label: "My Runs", icon: Truck, roles: ["field"], flag: "truck_driver", testId: "nav-my-runs" },
    { to: "/app/running-sheets", label: "Running Sheets", icon: ClipboardList, roles: ["field"], flag: "truck_driver", testId: "nav-driver-running-sheets" },
  ] },
  {
    id: "commercial",
    label: "Commercial",
    items: [
      { to: "/app/tenders", label: "Tenders", icon: FileText, roles: OFFICE_QS, testId: "nav-tenders" },
      { to: "/app/takeoffs", label: "Takeoffs", icon: PencilRuler, roles: OFFICE_QS, testId: "nav-takeoffs" },
      { to: "/app/quotes", label: "Quotes", icon: FileText, roles: OFFICE_QS, testId: "nav-quotes" },
      { to: "/app/invoicing", label: "Billing & Invoices", icon: Receipt, roles: OFFICE_QS, testId: "nav-billing" },
      { to: "/app/variations", label: "Variations", icon: FileSignature, roles: OFFICE_QS, testId: "nav-variations" },
      { to: "/app/financials", label: "Financials", icon: TrendingUp, roles: ["super_admin", "admin"], testId: "nav-financials" },
    ],
  },
  {
    id: "people",
    label: "People",
    items: [
      { to: "/app/team-hub", label: "Team", icon: UserCog, roles: OFFICE, testId: "nav-team" },
      { to: "/app/time", label: "Timesheets", icon: Clock, roles: OFFICE_FIELD, testId: "nav-timesheets" },
      { to: "/app/leave", label: "Leave", icon: CalendarDays, roles: OFFICE_FIELD, testId: "nav-leave" },
      { to: "/app/payroll", label: "Payroll", icon: Banknote, roles: OFFICE_FIELD, testId: "nav-payroll" },
      { to: "/app/competencies", label: "Competencies", icon: ShieldCheck, roles: OFFICE, testId: "nav-competencies" },
    ],
  },
  {
    id: "assets",
    label: "Plant",
    items: [
      { to: "/app/plant", label: "Plant & Fleet", icon: Truck, roles: ALL, testId: "nav-plant-fleet" },
      { to: "/app/fuel", label: "Fuel", icon: Fuel, roles: ALL, testId: "nav-fuel" },
    ],
  },
  {
    id: "safety",
    label: "Safety & QA",
    items: [
      { to: "/app/health-safety", label: "Health & Safety", icon: ShieldAlert, roles: OFFICE, testId: "nav-health-safety" },
      { to: "/app/my-safety", label: "My Safety", icon: ShieldCheck, roles: ["field"], testId: "nav-my-safety" },
      { to: "/app/safety-qa", label: "Safety & QA", icon: ShieldAlert, roles: ["field", "mechanic"], testId: "nav-safety-qa" },
    ],
  },
  {
    id: "contacts",
    label: "Contacts",
    items: [
      { to: "/app/clients", label: "Clients", icon: Users, roles: OFFICE_QS, testId: "nav-clients" },
      { to: "/app/suppliers", label: "Suppliers", icon: Phone, roles: [...OFFICE_FIELD, "quantity_surveyor"], testId: "nav-suppliers" },
    ],
  },
  {
    id: "more",
    label: "More",
    items: [
      {
        id: "reports", label: "Reports", icon: BarChart3, testId: "nav-reports-group",
        children: [
          { to: "/app/reports/monthly", label: "Monthly Report", icon: BarChart3, roles: OFFICE_QS, testId: "nav-reports" },
          { to: "/app/reports/job-billing", label: "Job Billing", icon: Receipt, roles: OFFICE_QS, testId: "nav-reports-job-billing" },
        ],
      },
      { to: "/app/jobs/archived", label: "Archived Jobs", icon: Archive, roles: OFFICE, testId: "nav-archived-jobs" },
      { to: "/app/sssps/archived", label: "Archived SSSPs", icon: Archive, roles: OFFICE, testId: "nav-archived-sssps" },
      { to: "/app/correspondence", label: "Correspondence", icon: Mail, roles: OFFICE_QS, testId: "nav-correspondence" },
      { to: "/app/company-documents", label: "Company Documents", icon: FileText, roles: OFFICE, testId: "nav-company-documents" },
      { to: "/app/rate-library", label: "Rate Library", icon: Library, roles: OFFICE_QS, testId: "nav-rate-library" },
      { to: "/app/business-profile", label: "Business Profile", icon: LayoutGrid, roles: OFFICE, testId: "nav-business-profile" },
      { to: "/app/announcements", label: "Notice Board", icon: Megaphone, roles: ALL, testId: "nav-announcements" },
      { to: "/app/import", label: "Import Data", icon: UploadCloud, roles: OFFICE, testId: "nav-import" },
      { to: "/app/onboarding", label: "Guided Onboarding", icon: Compass, roles: OFFICE_QS, testId: "nav-onboarding" },
      { to: "/app/admin/reviews", label: "Reviews", icon: Star, roles: ["super_admin"], testId: "nav-admin-reviews" },
      { to: "/app/video-support", label: "Video Support", icon: Video, roles: ["super_admin"], testId: "nav-video-support" },
    ],
  },
];

const itemVisible = (i, user) =>
  i.roles.includes(user.role) && (!i.flag || user.role !== "field" || !!user[i.flag]);

// ── Supply Yard workspace navigation (its own focused sidebar tree) ──────────
// Terminology cleanup (presentation only — routes/data unchanged):
//   "Products Accepted" → "Products" (saleable yard products the yard sells)
//   "Products Tipped"   → "Materials Accepted" (inbound material catalogue)
export const SUPPLY_YARD_GROUPS = [
  {
    id: "sy_home", label: null,
    items: [
      { to: "/app/supply-yard", label: "Overview", icon: Boxes, roles: SY_ALL, end: true, testId: "nav-sy-overview" },
    ],
  },
  {
    id: "sy_operations", label: "Operations",
    items: [
      { to: "/app/supply-yard/visits", label: "Yard Visits", icon: Truck, roles: SY_GATE, testId: "nav-sy-visits" },
      { to: "/app/supply-yard/loader-queue", label: "Loader Queue", icon: Package, roles: SY_LOADER, testId: "nav-sy-loader-queue" },
    ],
  },
  {
    id: "sy_materials", label: "Materials",
    items: [
      { to: "/app/supply-yard/products", label: "Products", icon: Package, roles: SY_ROLES, testId: "nav-sy-products" },
      { to: "/app/supply-yard/materials", label: "Materials Accepted", icon: Boxes, roles: SY_ROLES, testId: "nav-sy-materials" },
      { to: "/app/supply-yard/stockpiles", label: "Stockpiles", icon: Layers, roles: SY_ROLES, testId: "nav-sy-stockpiles" },
      { to: "/app/supply-yard/processing", label: "Processing", icon: Recycle, roles: SY_ROLES, testId: "nav-sy-processing" },
      { to: "/app/supply-yard/stock-control", label: "Stock Control", icon: SlidersHorizontal, roles: SY_ROLES, testId: "nav-sy-stock-control" },
    ],
  },
  {
    id: "sy_trade", label: "Trade",
    items: [
      { to: "/app/supply-yard/customers", label: "Customers", icon: Users, roles: SY_ROLES, testId: "nav-sy-customers" },
      { to: "/app/supply-yard/dockets", label: "Dockets", icon: ClipboardList, roles: SY_ROLES, testId: "nav-sy-dockets" },
    ],
  },
  {
    id: "sy_management", label: "Management",
    items: [
      { to: "/app/supply-yard/reports", label: "Reports", icon: BarChart3, roles: SY_ROLES, testId: "nav-sy-reports" },
      { to: "/app/supply-yard/settings", label: "Settings", icon: UserCog, roles: OFFICE, testId: "nav-sy-settings" },
    ],
  },
];

export const TRUCKING_GROUPS = [
  { id: "trucking_home", label: null, items: [
    { to: "/app/trucking", label: "Overview", icon: LayoutDashboard, roles: OFFICE, end: true, testId: "nav-trucking-overview" },
  ] },
  { id: "trucking_operations", label: "Operations", items: [
    { to: "/app/trucking/dispatch", label: "Dispatch", icon: Truck, roles: OFFICE, testId: "nav-trucking-dispatch" },
    { to: "/app/trucking/jobs", label: "Trucking Jobs", icon: Truck, roles: OFFICE, testId: "nav-trucking-jobs" },
    { to: "/app/trucking/running-sheets", label: "Running Sheets", icon: ClipboardList, roles: OFFICE, testId: "nav-trucking-running-sheets" },
    { to: "/app/trucking/live-tracking", label: "Live Tracking", icon: MapPin, roles: OFFICE, testId: "nav-trucking-live-tracking" },
  ] },
  { id: "trucking_commercial", label: "Commercial", items: [
    { to: "/app/trucking/ready-to-bill", label: "Ready to Bill", icon: Receipt, roles: OFFICE, testId: "nav-trucking-ready-to-bill" },
  ] },
  { id: "trucking_fleet", label: "Fleet", items: [
    { to: "/app/trucking/trucks", label: "Trucks", icon: Truck, roles: OFFICE, testId: "nav-trucking-trucks" },
  ] },
  { id: "trucking_management", label: "Management", items: [
    { to: "/app/trucking/reports", label: "Reports", icon: BarChart3, roles: OFFICE },
    { to: "/app/trucking/reconciliation", label: "Reconciliation", icon: ClipboardCheck, roles: OFFICE },
  ] },
];

// Existing safety modules, organised as one office workspace. Field workflows
// retain the Civil/Job entries and do not gain company-wide workspace access.
export const HEALTH_SAFETY_GROUPS = [
  { id: "hs_home", label: null, items: [
    { to: "/app/health-safety", label: "Overview", icon: LayoutDashboard, roles: OFFICE, end: true, testId: "nav-hs-overview" },
  ] },
  { id: "hs_site", label: "Site Safety", items: [
    { to: "/app/health-safety/sssps", label: "SSSPs", icon: FileCheck2, roles: OFFICE, testId: "nav-hs-sssps" },
    { to: "/app/health-safety/toolbox-talks", label: "Toolbox Talks", icon: ClipboardCheck, roles: OFFICE, testId: "nav-hs-toolbox" },
    { to: "/app/health-safety/pre-starts", label: "Pre-Starts", icon: Wrench, roles: OFFICE, testId: "nav-hs-prestarts" },
    { to: "/app/health-safety/inductions", label: "Inductions", icon: HardHat, roles: OFFICE, testId: "nav-hs-inductions" },
  ] },
  { id: "hs_risk", label: "Risk & Compliance", items: [
    { to: "/app/health-safety/hazards", label: "Hazards & Incidents", icon: ShieldAlert, roles: OFFICE, testId: "nav-hs-hazards" },
    { to: "/app/health-safety/hazsub", label: "HazSub", icon: FlaskConical, roles: OFFICE, testId: "nav-hs-hazsub" },
  ] },
  { id: "hs_assurance", label: "Assurance", items: [
    { to: "/app/health-safety/inspections", label: "Inspections", icon: ClipboardList, roles: OFFICE, testId: "nav-hs-inspections" },
    { to: "/app/health-safety/sitewise", label: "SiteWise Preparation", icon: ClipboardCheck, roles: OFFICE, testId: "nav-hs-sitewise" },
    { to: "/app/health-safety/qa", label: "QA", icon: ShieldCheck, roles: OFFICE, testId: "nav-hs-qa" },
    { to: "/app/health-safety/forms", label: "Forms", icon: ClipboardList, roles: OFFICE, testId: "nav-hs-forms" },
  ] },
  { id: "hs_people", label: "People", items: [
    { to: "/app/health-safety/competencies", label: "Competencies", icon: ShieldCheck, roles: OFFICE, testId: "nav-hs-competencies" },
  ] },
  { id: "hs_documents", label: "Documents", items: [
    { to: "/app/health-safety/documents", label: "Safety Documents", icon: FileText, roles: OFFICE, testId: "nav-hs-documents" },
  ] },
];

// ── Landfill Operations workspace (incoming dirt load intake) ────────────────
export const LANDFILL_GROUPS = [
  { id: "lf_home", label: null, items: [
    { to: "/app/landfill", label: "Overview", icon: LayoutDashboard, roles: OFFICE, end: true, testId: "nav-lf-overview" },
  ] },
  { id: "lf_operations", label: "Operations", items: [
    { to: "/app/landfill/loads", label: "Incoming Loads", icon: Truck, roles: OFFICE, testId: "nav-lf-loads" },
    { to: "/app/landfill/tipsites", label: "Tipsites", icon: MapPin, roles: OFFICE, testId: "nav-lf-tipsites" },
    { to: "/app/landfill/companies", label: "Companies", icon: Building2, roles: OFFICE, testId: "nav-lf-companies" },
    { to: "/app/landfill/invoices", label: "Invoices", icon: FileText, roles: OFFICE, testId: "nav-lf-invoices" },
    { to: "/app/landfill/rates", label: "Rates", icon: ClipboardList, roles: OFFICE, testId: "nav-lf-rates" },
    { to: "/app/landfill/trucks", label: "Trucks", icon: ClipboardList, roles: OFFICE, testId: "nav-lf-trucks" },
  ] },
];

// Workspace metadata for the WorkspaceSwitcher. Icons reuse existing lucide imports.
export const WORKSPACES = [
  { id: "civil", label: "Civil Operations", icon: HardHat, home: "/app",
    desc: "Jobs, crews, commercial, safety, plant and workforce." },
  { id: "trucking", label: "Trucking", icon: Truck, home: "/app/trucking",
    desc: "Dispatch, trucks, drivers and cartage operations." },
  { id: "supply_yard", label: "Supply Yard", icon: Boxes, home: "/app/supply-yard",
    desc: "Yard operations, materials, stock, dockets and processing." },
  { id: "landfill", label: "Landfill Operations", icon: Mountain, home: "/app/landfill",
    desc: "Log incoming dirt loads, carriers, soil type and condition." },
  { id: "health_safety", label: "Health & Safety", icon: ShieldCheck, home: "/app/health-safety",
    desc: "Company-wide site safety, workforce readiness and assurance." },
];

// Active workspace is derived from the route (survives hard refresh).
export function workspaceForPath(pathname) {
  if (pathname === "/app/health-safety" || pathname?.startsWith("/app/health-safety/")) return "health_safety";
  if (pathname === "/app/trucking" || pathname?.startsWith("/app/trucking/")) return "trucking";
  if (pathname === "/app/landfill" || pathname?.startsWith("/app/landfill/")) return "landfill";
  return pathname && pathname.startsWith("/app/supply-yard") ? "supply_yard" : "civil";
}

const groupsForWorkspace = (workspace) => (workspace === "health_safety" ? HEALTH_SAFETY_GROUPS : workspace === "trucking" ? TRUCKING_GROUPS : workspace === "landfill" ? LANDFILL_GROUPS : workspace === "supply_yard" ? SUPPLY_YARD_GROUPS : NAV_GROUPS);

// Returns groups filtered for the user within a workspace. Submenus with one
// visible child are flattened; empty submenus/groups disappear entirely.
export function filterNavGroups(user, workspace = "civil") {
  return groupsForWorkspace(workspace)
    .map((g) => ({
      ...g,
      items: g.items
        .map((i) => {
          if (!i.children) return itemVisible(i, user) ? i : null;
          const kids = i.children.filter((c) => itemVisible(c, user));
          if (!kids.length) return null;
          if (kids.length === 1) return { ...kids[0] };
          return { ...i, children: kids };
        })
        .filter(Boolean),
    }))
    .filter((g) => g.items.length > 0);
}

// Workspaces the user can actually access (has ≥1 visible item), reusing the
// existing role gates — no separate RBAC.
export function availableWorkspaces(user) {
  if (!user) return [];
  return WORKSPACES.filter((w) => filterNavGroups(user, w.id).length > 0);
}

// Routes accessed outside the sidebar (profile menu, top bar, detail pages).
const EXTRA_CRUMBS = {
  "/app/inbox": ["Notifications"],
  "/app/chat": ["Chat"],
  "/app/profile": ["Account", "My Profile"],
  "/app/branding": ["Company", "Company Settings"],
  "/app/billing": ["Company", "Billing"],
  "/app/integrations/xero": ["Company", "Xero Integration"],
  "/app/help": ["Help", "Help Centre"],
  "/app/onboarding": ["Guided Onboarding"],
  "/app/support": ["Help", "Support"],
  "/app/changelog": ["Help", "What's New"],
  "/app/platform": ["Platform Administration"],
  "/app/financials": ["Commercial", "Financials"],
};

// Breadcrumb trail for a pathname, derived from the nav tree.
// e.g. /app/customer-invoices → ["Commercial", "Invoices", "Customer Invoices"]
export function breadcrumbFor(pathname) {
  if (pathname === "/app" || pathname === "/app/") return ["Overview"];
  if (pathname === "/app/health-safety" || pathname === "/app/health-safety/") return ["Health & Safety", "Overview"];
  let best = null; // {len, trail}
  const consider = (to, trail, end) => {
    const hit = end ? pathname === to : pathname === to || pathname.startsWith(to + "/") || pathname.startsWith(to + "?");
    if (hit && (!best || to.length > best.len)) best = { len: to.length, trail };
  };
  for (const g of [...NAV_GROUPS, ...SUPPLY_YARD_GROUPS, ...TRUCKING_GROUPS, ...LANDFILL_GROUPS, ...HEALTH_SAFETY_GROUPS]) {
    for (const i of g.items) {
      if (i.children) {
        for (const c of i.children) consider(c.to, [g.label, i.label, c.label].filter(Boolean), c.end);
      } else {
        consider(i.to, [g.label, i.label].filter(Boolean), i.end);
      }
    }
  }
  for (const [to, trail] of Object.entries(EXTRA_CRUMBS)) consider(to, trail, false);
  if (!best) return null;
  if (workspaceForPath(pathname) === "health_safety") return ["Health & Safety", ...best.trail.filter(Boolean)];
  // Drop consecutive duplicate labels (a hub's group + item can share a name).
  return best.trail.filter((v, idx) => v !== best.trail[idx - 1]);
}
